import { toastsWithIntl } from "../toastService";
import { Organization } from "../organization/organizationModel";
import {
  acceptPremiumSponsorshipRequest,
  declinePremiumSponsorshipRequest,
  deletePremiumSponsorshipRequest,
  getMyPremiumSponsorshipsRequest,
  getPremiumSponsorshipByTokenRequest,
  getPremiumSponsorshipsRequest,
  invitePremiumSponsorshipsRequest,
  renouncePremiumSponsorshipRequest,
  resendPremiumSponsorshipInvitationRequest,
  revokePremiumSponsorshipRequest,
} from "./premiumSponsorshipApi";
import {
  InvitePremiumSponsorshipsResult,
  PremiumSponsorship,
  PremiumSponsorshipPreview,
} from "./premiumSponsorshipModel";

/**
 * Mirrors `availableCredits` in the back: `maxSponsorshipsCount` is a fixed
 * quota, and what's left is that quota minus the sponsorships currently in
 * flight. Deriving it — rather than reading a stored counter — is what keeps
 * the figure correct whatever happens to the sponsorships. Declined and
 * revoked ones hand their credit back by simply not being counted.
 */
export const countAvailableSponsorshipCredits = (
  organization: Organization,
  sponsorships: PremiumSponsorship[],
): number =>
  organization.maxSponsorshipsCount -
  sponsorships.filter(
    (sponsorship) =>
      sponsorship.status === "invited" || sponsorship.status === "accepted",
  ).length;

export interface PremiumSponsorshipService {
  invitePremiumSponsorships: (
    organizationId: Organization["id"],
    emails: string[],
  ) => Promise<InvitePremiumSponsorshipsResult>;
  getPremiumSponsorships: (
    organizationId: Organization["id"],
  ) => Promise<PremiumSponsorship[]>;
  revokePremiumSponsorship: (
    organizationId: Organization["id"],
    id: PremiumSponsorship["id"],
  ) => Promise<void>;
  resendPremiumSponsorshipInvitation: (
    organizationId: Organization["id"],
    id: PremiumSponsorship["id"],
  ) => Promise<void>;
  deletePremiumSponsorship: (
    organizationId: Organization["id"],
    id: PremiumSponsorship["id"],
  ) => Promise<void>;
  getPremiumSponsorshipByToken: (
    token: string,
  ) => Promise<PremiumSponsorshipPreview>;
  declinePremiumSponsorship: (token: string) => Promise<void>;
  acceptPremiumSponsorship: (token: string) => Promise<void>;
  getMyPremiumSponsorships: () => Promise<PremiumSponsorship[]>;
  renouncePremiumSponsorship: (id: PremiumSponsorship["id"]) => Promise<void>;
}

export const premiumSponsorshipService = (): PremiumSponsorshipService => {
  const { toastSuccess: toastOrgSuccess, toastError: toastOrgError } =
    toastsWithIntl(["organization"]);
  const { toastSuccess: toastAccountSuccess, toastError: toastAccountError } =
    toastsWithIntl(["accounts"]);

  const invitePremiumSponsorships: PremiumSponsorshipService["invitePremiumSponsorships"] =
    (organizationId, emails) => {
      return invitePremiumSponsorshipsRequest(organizationId, emails).then(
        (result) => {
          toastOrgSuccess("organization:premium-sponsorship.invite.SUCCESS");
          return result;
        },
        (err) => {
          toastOrgError("organization:premium-sponsorship.invite.ERROR");
          return Promise.reject(err);
        },
      );
    };

  const getPremiumSponsorships: PremiumSponsorshipService["getPremiumSponsorships"] =
    (organizationId) => {
      return getPremiumSponsorshipsRequest(organizationId);
    };

  const revokePremiumSponsorship: PremiumSponsorshipService["revokePremiumSponsorship"] =
    (organizationId, id) => {
      return revokePremiumSponsorshipRequest(organizationId, id).then(
        () => {
          toastOrgSuccess("organization:premium-sponsorship.revoke.SUCCESS");
        },
        (err) => {
          toastOrgError("organization:premium-sponsorship.revoke.ERROR");
          return Promise.reject(err);
        },
      );
    };

  const resendPremiumSponsorshipInvitation: PremiumSponsorshipService["resendPremiumSponsorshipInvitation"] =
    (organizationId, id) => {
      return resendPremiumSponsorshipInvitationRequest(organizationId, id).then(
        () => {
          toastOrgSuccess("organization:premium-sponsorship.resend.SUCCESS");
        },
        (err) => {
          toastOrgError("organization:premium-sponsorship.resend.ERROR");
          return Promise.reject(err);
        },
      );
    };

  const deletePremiumSponsorship: PremiumSponsorshipService["deletePremiumSponsorship"] =
    (organizationId, id) => {
      return deletePremiumSponsorshipRequest(organizationId, id).then(
        () => {
          toastOrgSuccess("organization:premium-sponsorship.delete.SUCCESS");
        },
        (err) => {
          toastOrgError("organization:premium-sponsorship.delete.ERROR");
          return Promise.reject(err);
        },
      );
    };

  const getPremiumSponsorshipByToken: PremiumSponsorshipService["getPremiumSponsorshipByToken"] =
    (token) => {
      return getPremiumSponsorshipByTokenRequest(token);
    };

  const declinePremiumSponsorship: PremiumSponsorshipService["declinePremiumSponsorship"] =
    (token) => {
      return declinePremiumSponsorshipRequest(token).then(
        () => {
          toastAccountSuccess("accounts:premium-sponsorship.decline.SUCCESS");
        },
        (err) => {
          toastAccountError("accounts:premium-sponsorship.decline.ERROR");
          return Promise.reject(err);
        },
      );
    };

  const acceptPremiumSponsorship: PremiumSponsorshipService["acceptPremiumSponsorship"] =
    (token) => {
      return acceptPremiumSponsorshipRequest(token).then(
        () => {
          toastAccountSuccess("accounts:premium-sponsorship.accept.SUCCESS");
        },
        (err) => {
          // 403 = invited on another address than the logged-in account: not
          // a failure to report as such, the consent screen explains it
          // inline right where the candidate clicked.
          // 401 = the session died before the click; `api.ts` has already
          // logged the candidate out, and the caller sends them to login —
          // where the sponsorship gets accepted. A red toast would only add
          // noise to a flow that recovers on its own.
          if (![401, 403].includes(err?.response?.status)) {
            toastAccountError("accounts:premium-sponsorship.accept.ERROR");
          }
          return Promise.reject(err);
        },
      );
    };

  const getMyPremiumSponsorships: PremiumSponsorshipService["getMyPremiumSponsorships"] =
    () => {
      return getMyPremiumSponsorshipsRequest();
    };

  const renouncePremiumSponsorship: PremiumSponsorshipService["renouncePremiumSponsorship"] =
    (id) => {
      return renouncePremiumSponsorshipRequest(id).then(
        () => {
          toastAccountSuccess("accounts:premium-sponsorship.renounce.SUCCESS");
        },
        (err) => {
          toastAccountError("accounts:premium-sponsorship.renounce.ERROR");
          return Promise.reject(err);
        },
      );
    };

  return {
    invitePremiumSponsorships,
    getPremiumSponsorships,
    revokePremiumSponsorship,
    resendPremiumSponsorshipInvitation,
    deletePremiumSponsorship,
    getPremiumSponsorshipByToken,
    declinePremiumSponsorship,
    acceptPremiumSponsorship,
    getMyPremiumSponsorships,
    renouncePremiumSponsorship,
  };
};
